import type { DetectorOptions } from 'i18next-browser-languagedetector';

// Single source of truth for how we detect and persist the UI language.
//
// The previous config used `caches: ['localStorage']` with `lookupLocalStorage:
// 'ari-language'`, so the detector wrote whatever it resolved — including the
// `en` fallback — into the very key it reads first. One load that briefly
// resolved to English left `ari-language: en` behind, and from then on
// localStorage shadowed navigator permanently: the browser language was never
// consulted again and login was stuck in English.
//
// Fix: read an explicit-choice key the detector NEVER writes (`caches: []`), and
// persist a language only when the user explicitly selects one. The old
// `ari-language` key is intentionally abandoned, so previously-poisoned users
// fall back to navigator detection again.

export const SELECTED_LANGUAGE_KEY = 'ari-language-selected';

// Canonicalize a BCP-47 tag to one of our supported runtime codes. Special
// cases (Cantonese, Traditional Chinese, Bokmål) come before the generic
// region strip so zh-TW/yue are never collapsed to zh, and the Norwegian
// macro tag `no`/`nb-NO` resolves to `nb` (the code Lokalise and the browser use).
export function normalizeLocale(tag: string): string {
  if (!tag) return tag;
  const lower = tag.toLowerCase();
  if (lower === 'zh-tw' || lower.startsWith('zh-hant')) return 'zh-TW';
  if (lower === 'zh-hk' || lower === 'zh-mo' || lower.startsWith('yue')) return 'yue';
  if (lower === 'zh' || lower === 'zh-cn' || lower === 'zh-sg' || lower.startsWith('zh-hans')) return 'zh';
  if (lower === 'no' || lower === 'nb' || lower.startsWith('nb-') || lower.startsWith('no-')) return 'nb';
  return lower.split('-')[0];
}

export const languageDetectionOptions: DetectorOptions = {
  order: ['localStorage', 'navigator'],
  lookupLocalStorage: SELECTED_LANGUAGE_KEY,
  caches: [], // never auto-cache; only explicit selection persists (below)
};

// Persist an explicit user language choice so it sticks across reload/login.
// The detector reads SELECTED_LANGUAGE_KEY first, so this wins over navigator.
export function persistSelectedLanguage(lng: string): void {
  try {
    window.localStorage.setItem(SELECTED_LANGUAGE_KEY, lng);
  } catch {
    // localStorage unavailable (private mode / SSR) — the change still applies
    // in-memory via i18n.changeLanguage; nothing else to do.
  }
}
